import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
const baseURL = process.env.REACT_APP_BASE_URL;

interface DepositBonusItem {
  deposit_no: number;
  minimum_deposit: number;
  bonus_percent: number;
}

const DepositBonus = () => {
  const [depositBonuses, setDepositBonuses] = useState<DepositBonusItem[]>([]);
  const [newBonus, setNewBonus] = useState<DepositBonusItem>({
    deposit_no: 0,
    minimum_deposit: 0,
    bonus_percent: 0,
  });
  const [editBonus, setEditBonus] = useState<DepositBonusItem | null>(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    navigate('/login');
  };

  // Fetch deposit bonuses from the API
  const fetchDepositBonuses = async () => {
    try {
      const response = await axios.get(`${baseURL}/admin/getDepositBonus`);
      if (response.data) {
        setDepositBonuses(response.data.data);
      } else {
        toast('Failed to fetch deposit bonuses.');
      }
    } catch (error) {
      console.error('Error fetching deposit bonuses:', error);
      toast('Error fetching deposit bonuses.');
    }
  };

  useEffect(() => {
    fetchDepositBonuses();
  }, []);

  // Handle input changes for new bonus
  const handleNewBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewBonus((prevBonus) => ({
      ...prevBonus,
      [name]: value ? parseInt(value) : 0,
    }));
  };

  // Handle input changes for editing bonus
  const handleEditBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editBonus) {
      const { name, value } = e.target;
      setEditBonus((prevBonus) => ({
        ...prevBonus,
        [name]: value ? parseInt(value) : 0,
      }));
    }
  };

  // Create a new deposit bonus
  const handleAddBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBonus?.minimum_deposit || !newBonus?.bonus_percent) {
      toast('Please provide valid inputs for the new bonus.');
      return;
    }

    try {
      const response = await axios.post(
        `${baseURL}/admin/createDepositBonus`,
        newBonus
      );
      if (response.data.status) {
        toast('Deposit bonus added successfully!');
        setNewBonus({
          deposit_no: 0,
          minimum_deposit: 0,
          bonus_percent: 0,
        });
        fetchDepositBonuses();
      } else {
        toast(response.data.message);
      }
    } catch (error: any) {
      console.error('Error adding deposit bonus:', error);
      toast('Error adding deposit bonus.');
      toast(error.response.data.message);
    }
  };

  // Update an existing deposit bonus
  const handleUpdateBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editBonus && (editBonus.minimum_deposit && editBonus.bonus_percent)) {
      try {
        const response = await axios.post(
          `${baseURL}/admin/updateDepositBonus/${editBonus.deposit_no}`,
          editBonus
        );
        if (response.data) {
          toast('Deposit bonus updated successfully!');
          setEditBonus(null); // Clear the edit state
          fetchDepositBonuses();
        } else {
          toast('Failed to update deposit bonus.');
        }
      } catch (error) {
        console.error('Error updating deposit bonus:', error);
        toast('Error updating deposit bonus.');
      }
    } else {
      toast('Please provide valid inputs to update the bonus.');
    }
  };

  // Delete a deposit bonus
  const handleDeleteBonus = async (deposit_no: number) => {
    try {
      const response = await axios.delete(
        `${baseURL}/admin/deleteDepositBonus/${deposit_no}`
      );
      if (response.data.status) {
        toast('Deposit bonus deleted successfully!');
        fetchDepositBonuses();
      } else {
        toast('Failed to delete deposit bonus.');
      }
    } catch (error) {
      console.error('Error deleting deposit bonus:', error);
      toast('Error deleting deposit bonus.');
    }
  };

  return (
    <div className='ml-0 lg:ml-[256px] p-4'>
      <h2 className='text-2xl font-semibold mb-4 text-white'>Deposit Bonuses</h2>
      <hr className='p-5' />

      {/* Add New Bonus Form */}
      <div className='mb-8'>
        <h3 className='text-xl text-white'>Add New Deposit Bonus</h3>
        <form onSubmit={handleAddBonus} className='space-y-4'>
          <div>
            <label className='text-white'>Deposit No</label>
            <input
              type='number'
              name='deposit_no'
              value={newBonus.deposit_no}
              onChange={handleNewBonusChange}
              className='border rounded p-2 w-full'
            />
          </div>
          <div>
            <label className='text-white'>Minimum Deposit</label>
            <input
              type='number'
              name='minimum_deposit'
              value={newBonus.minimum_deposit}
              onChange={handleNewBonusChange}
              className='border rounded p-2 w-full'
            />
          </div>
          <div>
            <label className='text-white'>Bonus Percentage</label>
            <input
              type='number'
              name='bonus_percent'
              value={newBonus.bonus_percent}
              onChange={handleNewBonusChange}
              className='border rounded p-2 w-full'
            />
          </div>
          <button
            type='submit'
            className='bg-blue-500 text-white py-2 px-4 rounded'
          >
            Add Bonus
          </button>
        </form>
      </div>

      {/* Edit Existing Bonus Form */}
      {editBonus && (
        <div className='mb-8'>
          <h3 className='text-xl text-white'>Edit Deposit Bonus</h3>
          <form onSubmit={handleUpdateBonus} className='space-y-4'>
            <div>
              <label className='text-white'>Deposit No (Cannot be changed)</label>
              <input
                type='text'
                name='deposit_no'
                value={editBonus?.deposit_no}
                disabled
                className='border rounded p-2 w-full bg-gray-300'
              />
            </div>
            <div>
              <label className='text-white'>Minimum Deposit</label>
              <input
                type='number'
                name='minimum_deposit'
                value={editBonus?.minimum_deposit}
                onChange={handleEditBonusChange}
                className='border rounded p-2 w-full'
              />
            </div>
            <div>
              <label className='text-white'>Bonus Percentage</label>
              <input
                type='number'
                name='bonus_percent'
                value={editBonus?.bonus_percent}
                onChange={handleEditBonusChange}
                className='border rounded p-2 w-full'
              />
            </div>
            <button
              type='submit'
              className='bg-green-500 text-white py-2 px-4 rounded'
            >
              Update Bonus
            </button>
            <button
              type='button'
              onClick={() => setEditBonus(null)}
              className='bg-red-500 text-white py-2 px-4 rounded ml-2'
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Display Deposit Bonuses */}
      <div>
        {depositBonuses.length > 0 ? (
          <table className='table-auto w-full text-white text-center'>
            <thead>
              <tr>
                <th className='border px-4 py-2'>Deposit No</th>
                <th className='border px-4 py-2'>Minimum Deposit</th>
                <th className='border px-4 py-2'>Bonus Percentage</th>
                <th className='border px-4 py-2'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {depositBonuses?.map((bonus) => (
                <tr key={bonus.deposit_no}>
                  <td className='border px-4 py-2'>{bonus.deposit_no}st Deposit</td>
                  <td className='border px-4 py-2'>{bonus.minimum_deposit}</td>
                  <td className='border px-4 py-2'>{bonus.bonus_percent}%</td>
                  <td className='border px-4 py-2'>
                    <button
                      onClick={() => setEditBonus(bonus)}
                      className='bg-yellow-500 text-white py-1 px-3 rounded'
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBonus(bonus.deposit_no)}
                      className='bg-red-500 text-white py-1 px-3 rounded ml-2'
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className='text-white'>No deposit bonuses available at the moment.</p>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default DepositBonus;
